/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';

import { NativeSelect } from '@/components/ui/native-select';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { buildUrl, useSite } from '@salesforce/storefront-next-runtime/site-context';
import { resourceRoutes, routes } from '@/route-paths';

export default function SiteSwitcher(): ReactElement | null {
    const id = useId();
    const { t, i18n } = useTranslation('siteSwitcher');
    const fetcher = useFetcher();
    const config = useConfig();
    const { site } = useSite();
    const sites = [...config.commerce.sites].sort((a, b) =>
        t(`sites.${a.id}`, { defaultValue: a.id }).localeCompare(
            t(`sites.${b.id}`, { defaultValue: b.id }),
            i18n.language
        )
    );

    if (sites.length <= 1) {
        return null;
    }

    const handleSiteChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const siteId = e.target.value;
        const targetSite = sites.find((s) => s.id === siteId);
        if (!targetSite) {
            return;
        }

        const siteRef = config.siteAliasMap?.[siteId] ?? siteId;
        const localeRef = config.localeAliasMap?.[targetSite.defaultLocale] ?? targetSite.defaultLocale;

        const pathname = buildUrl({
            to: routes.home,
            urlConfig: config.url,
            params: { siteId: siteRef, localeId: localeRef },
        });

        const formData = new FormData();
        formData.append('type', 'site');
        formData.append('payload', JSON.stringify({ siteId, pathname }));

        await i18n.changeLanguage(targetSite.defaultLocale);

        await fetcher.submit(formData, {
            method: 'POST',
            action: resourceRoutes.setSiteContext,
        });
        window.location.href = pathname;
    };

    return (
        <div className="*:not-first:mt-2">
            <NativeSelect
                id={id}
                onChange={(e) => void handleSiteChange(e)}
                aria-label={t('ariaLabel')}
                value={site?.id ?? ''}>
                {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                        {t(`sites.${s.id}`, { defaultValue: s.id })}
                    </option>
                ))}
            </NativeSelect>
        </div>
    );
}
